import {
  SiderBarHeader,
  SidebarContentWrapper,
  SidebarStyledDivider,
} from "Components/UI/Sidebar";
import {
  Box,
  Button,
  Tab,
  Tabs,
  Typography,
  styled,
  Switch,
} from "@mui/material";
import { TextValidator } from "react-material-ui-form-validator";
import { ApiLeadStatusRule, ApiProject } from "@incendium/api";
import produce from "immer";
import { Assignment, AssignmentTurnedIn } from "@mui/icons-material";
import { useMemo, useState } from "react";
import { ApiURL } from "config";
import { StyledFlexBox } from "Components/UI/StylesFlexBox";
import { ClickableTooltip } from "Components/ClickableTooltip";
import { StyledHelp } from "Components/ClickableTooltip/StyledHelp";
import StyledDrawer, {
  StyledDrawerContainer,
  StyledDrawerTitle,
} from "Components/UI/StyledDrawer";
import CopyClipboardBtn from "Components/CopyClipboardBtn/CopyClipboardBtn";
import { useOAuthCredentials } from "Hooks/useOAuthCredentials";

const StyledPre = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  fontFamily: "monospace",
  fontSize: "12px",
  padding: "16px",
  borderRadius: "4px",
  border: `1px solid ${theme.palette.divider}`,
  whiteSpace: "pre-wrap",
}));

interface ILeadStatusRuleSidebarProps {
  leadStatusRule: ApiLeadStatusRule;
  setLeadStatusRule: React.Dispatch<
    React.SetStateAction<ApiLeadStatusRule | undefined>
  >;
  project: ApiProject;
  ledStatusRules: ApiLeadStatusRule[];
}

function LeadStatusRuleSidebar({
  leadStatusRule,
  setLeadStatusRule,
  project,
  ledStatusRules,
}: ILeadStatusRuleSidebarProps) {
  // note, user still needs lead key to append status
  const apiHost = (() => {
    try {
      return new URL(ApiURL).host;
    } catch {
      return "__apiHost__";
    }
  })();
  const [apiCopied, setApiCopied] = useState(false);
  const [phpCopied, setPhpCopied] = useState(false);
  const [goCopied, setGoCopied] = useState(false);
  const [nodeCopied, setNodeCopied] = useState(false);
  const [offlineApiDrawerOpen, setOfflineApiDrawerOpen] = useState(false);
  const [offlineApiTab, setOfflineApiTab] = useState<
    "curl" | "php" | "node" | "go"
  >("curl");
  const [incCopied, setIncCopied] = useState(false);
  const { credentials, loading: credentialsLoading } = useOAuthCredentials();
  const resolvedProjectKey = useMemo(
    () => project.key || "__projectKey__",
    [project.key],
  );
  const resolvedApiKey = useMemo(
    () => credentials.apiKey || "__apiKey__",
    [credentials.apiKey],
  );
  const resolvedApiSecret = useMemo(
    () => credentials.apiSecret || "__apiSecret__",
    [credentials.apiSecret],
  );

  const curlSnippet = useMemo(
    () =>
      `# Step 1: Exchange your API key and API secret for an access token.
ACCESS_TOKEN=$(curl -s "${ApiURL}/services/oauth/v1/${resolvedApiKey}/request-token?clientSecret=${resolvedApiSecret}" | jq -r '.token.accessToken')

# Step 2: Call the endpoint with the access token as a Bearer token.
# Replace __leadKey__ with your lead key.
curl --request GET \\
  "${ApiURL}/l/als?pk=${resolvedProjectKey}&lk=__leadKey__&sk=${leadStatusRule.stageKey || ""}" \\
  --header "Authorization: Bearer \${ACCESS_TOKEN}"`,
    [
      leadStatusRule.stageKey,
      resolvedApiKey,
      resolvedApiSecret,
      resolvedProjectKey,
    ],
  );
  const phpSnippet = useMemo(
    () => `<?php
$apiBase = "${ApiURL}";
$apiKey = "${resolvedApiKey}";
$apiSecret = "${resolvedApiSecret}";
$projectKey = "${resolvedProjectKey}";
$leadKey = "__leadKey__"; // replace with your lead key
$stageKey = "${leadStatusRule.stageKey || ""}";

// Step 1: Exchange your API key and API secret for an access token.
$tokenUrl = $apiBase
    . "/services/oauth/v1/" . rawurlencode($apiKey)
    . "/request-token?clientSecret=" . rawurlencode($apiSecret);
$tokenResponse = file_get_contents($tokenUrl);
if ($tokenResponse === false) {
    throw new Exception("Failed to get access token");
}
$tokenData = json_decode($tokenResponse, true);
$accessToken = $tokenData['token']['accessToken'];

// Step 2: Call the endpoint with the access token as a Bearer token.
$url = $apiBase
    . "/l/als?pk=" . rawurlencode($projectKey)
    . "&lk=" . rawurlencode($leadKey)
    . "&sk=" . rawurlencode($stageKey);

$context = stream_context_create([
    'http' => [
        'header' => "Authorization: Bearer " . $accessToken,
    ],
]);
$response = file_get_contents($url, false, $context);
if ($response === false) {
    throw new Exception("Failed to append lead status");
}

echo $response;`,
    [
      leadStatusRule.stageKey,
      resolvedApiKey,
      resolvedApiSecret,
      resolvedProjectKey,
    ],
  );
  const nodeSnippet = useMemo(
    () => `const apiBase = "${ApiURL}";
const apiKey = "${resolvedApiKey}";
const apiSecret = "${resolvedApiSecret}";
const projectKey = "${resolvedProjectKey}";
const leadKey = "__leadKey__"; // replace with your lead key
const stageKey = "${leadStatusRule.stageKey || ""}";

// Step 1: Exchange your API key and API secret for an access token.
const tokenUrl = apiBase
  + "/services/oauth/v1/" + encodeURIComponent(apiKey)
  + "/request-token?clientSecret=" + encodeURIComponent(apiSecret);
const tokenResponse = await fetch(tokenUrl);
if (!tokenResponse.ok) {
  throw new Error("Failed to get access token");
}
const { token } = await tokenResponse.json();
const accessToken = token.accessToken;

// Step 2: Call the endpoint with the access token as a Bearer token.
const url = new URL(apiBase + "/l/als");
url.searchParams.set("pk", projectKey);
url.searchParams.set("lk", leadKey);
url.searchParams.set("sk", stageKey);

const response = await fetch(url, {
  headers: {
    Authorization: "Bearer " + accessToken,
  },
});
if (!response.ok) {
  throw new Error("Failed to append lead status");
}

console.log(await response.text());`,
    [
      leadStatusRule.stageKey,
      resolvedApiKey,
      resolvedApiSecret,
      resolvedProjectKey,
    ],
  );
  const goSnippet = useMemo(
    () => `package main

import (
	"log"

	"bitbucket.org/incendiumai/incendium-go/client"
	"bitbucket.org/incendiumai/incendium-go/models"
	"bitbucket.org/incendiumai/incendium-go/services/lead_service"
)

func main() {
	// The SDK exchanges your API key and secret for an access token automatically.
	// New(host, schema, clientKey, clientSecret)
	// host example: api.incendium.ai (no scheme)
	incendiumClient := client.New(
		"${apiHost}",
		"https",
		"${credentials.apiKey || "__apiKey__"}",
		"${credentials.apiSecret || "__apiSecret__"}",
	)

	params := lead_service.NewLeadServiceAppendLeadStatusParams().
		WithProjectKey("${project.key}").
		WithPayload(&models.APILeadStatusPayload{
			LeadKey:  "__leadKey__",
			StageKey: "${leadStatusRule.stageKey || ""}",
		})

	_, err := incendiumClient.LeadService.LeadServiceAppendLeadStatus(params)
	if err != nil {
		log.Fatal(err)
	}
}`,
    [
      apiHost,
      leadStatusRule.stageKey,
      credentials.apiKey,
      credentials.apiSecret,
      project.key,
    ],
  );
  const incSnippet = `inc("append_lead_status", "${leadStatusRule.stageKey || ""}")`;
  const incGoSnippet = `inc("go")`;

  const anyHasDefault = ledStatusRules.some((rule) => rule.asDefault);

  return (
    <>
      <SiderBarHeader>
        <Typography variant="h2">Funnel Stage Information</Typography>
      </SiderBarHeader>
      <SidebarContentWrapper px={3}>
        <Box mb={3.5}>
          <TextValidator
            label="Name of funnel stage"
            variant="outlined"
            name="name"
            fullWidth
            validators={["required"]}
            errorMessages={["name is required"]}
            value={leadStatusRule.name || ""}
            onChange={(e: React.FormEvent<HTMLInputElement>) => {
              setLeadStatusRule(
                produce(leadStatusRule, (draft) => {
                  draft.name = e.currentTarget.value;
                }),
              );
            }}
          />
        </Box>
        {(leadStatusRule.asDefault || !anyHasDefault) && (
          <StyledFlexBox mb={4}>
            <Box display="flex" alignItems="center">
              <Switch
                checked={leadStatusRule.asDefault || false}
                onChange={(e, checked) => {
                  setLeadStatusRule(
                    produce(leadStatusRule, (draft) => {
                      draft.asDefault = checked;
                    }),
                  );
                }}
              />
              <Typography color={"primary"}>Set as default</Typography>
            </Box>
            <ClickableTooltip
              placement="bottom"
              width={335}
              text="Should a new lead be this funnel stage as default?"
              icon={<StyledHelp />}
            />
          </StyledFlexBox>
        )}

        <Box mb={2}>
          <SidebarStyledDivider />
        </Box>
        <Box mb={2}>
          <Typography variant="subtitle2" gutterBottom>
            Offline
          </Typography>
          <Typography variant="body2">
            The offline API URL is used to append the lead's funnel stage to the
            lead from your backend, note the unique lead key is required.
          </Typography>
        </Box>
        <Box mb={2}>
          <Button fullWidth onClick={() => setOfflineApiDrawerOpen(true)}>
            View API Instructions
          </Button>
        </Box>
        <Box mb={2}>
          <SidebarStyledDivider />
        </Box>
        <Box mb={2}>
          <Typography variant="subtitle2" gutterBottom>
            On Site
          </Typography>
          <Typography variant="body2">
            The inc javascript snippet is used to append the lead's funnel stage
            to the lead from your platform. Just copy and paste the snippet into
            yours pages html when you want to append the lead's funnel stage.
          </Typography>
        </Box>
        <Box mb={2}>
          <Button
            fullWidth
            onClick={() => {
              setIncCopied(true);
              navigator.clipboard.writeText(`${incSnippet}\n${incGoSnippet}`);
            }}
            endIcon={incCopied ? <AssignmentTurnedIn /> : <Assignment />}
          >
            Copy INC Function
          </Button>
        </Box>
      </SidebarContentWrapper>
      <StyledDrawer
        open={offlineApiDrawerOpen}
        onClose={() => setOfflineApiDrawerOpen(false)}
        maxWidth={700}
      >
        <StyledDrawerTitle>
          <Typography variant="subtitle1">Offline API Instructions</Typography>
          <Typography variant="body2">
            Use these examples from your backend to append a lead to this funnel
            stage.
          </Typography>
        </StyledDrawerTitle>
        <StyledDrawerContainer>
          <Tabs
            value={offlineApiTab}
            onChange={(_, value) => setOfflineApiTab(value)}
            variant="fullWidth"
          >
            <Tab label="cURL" value="curl" />
            <Tab label="PHP" value="php" />
            <Tab label="Node.js" value="node" />
            <Tab label="Golang" value="go" />
          </Tabs>
          {credentialsLoading && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2 }}>
              Loading credentials...
            </Typography>
          )}
          {offlineApiTab !== "go" && (
            <Box component="ol" sx={{ mt: 2, pl: 2.5, mb: 0 }}>
              <Typography component="li" variant="body2" gutterBottom>
                Exchange your API key and API secret for an access token via the
                OAuth endpoint.
              </Typography>
              <Typography component="li" variant="body2">
                Use{" "}
                <Box component="span" fontFamily="monospace">
                  token.accessToken
                </Box>{" "}
                from that response as a Bearer token when calling the append
                endpoint. Replace{" "}
                <Box component="span" fontFamily="monospace">
                  __leadKey__
                </Box>{" "}
                with your lead key.
              </Typography>
            </Box>
          )}
          {offlineApiTab === "go" && (
            <Typography variant="body2" sx={{ mt: 2 }} gutterBottom>
              The Go SDK handles access token exchange for you. Pass your API
              key and API secret when creating the client, then call{" "}
              <Box component="span" fontFamily="monospace">
                LeadServiceAppendLeadStatus
              </Box>{" "}
              with your lead key and this stage key.
            </Typography>
          )}
          {offlineApiTab === "curl" && (
            <Box mt={2}>
              <StyledPre>{curlSnippet}</StyledPre>
              <Box mt={2}>
                <CopyClipboardBtn
                  copied={apiCopied}
                  setCopied={setApiCopied}
                  text={curlSnippet}
                  variant="outlined"
                >
                  Copy cURL
                </CopyClipboardBtn>
              </Box>
            </Box>
          )}
          {offlineApiTab === "php" && (
            <Box mt={2}>
              <StyledPre>{phpSnippet}</StyledPre>
              <Box mt={2}>
                <CopyClipboardBtn
                  copied={phpCopied}
                  setCopied={setPhpCopied}
                  text={phpSnippet}
                  variant="outlined"
                >
                  Copy PHP
                </CopyClipboardBtn>
              </Box>
            </Box>
          )}
          {offlineApiTab === "node" && (
            <Box mt={2}>
              <StyledPre>{nodeSnippet}</StyledPre>
              <Box mt={2}>
                <CopyClipboardBtn
                  copied={nodeCopied}
                  setCopied={setNodeCopied}
                  text={nodeSnippet}
                  variant="outlined"
                >
                  Copy Node.js
                </CopyClipboardBtn>
              </Box>
            </Box>
          )}
          {offlineApiTab === "go" && (
            <Box mt={2}>
              <StyledPre>{goSnippet}</StyledPre>
              <Box mt={2}>
                <CopyClipboardBtn
                  copied={goCopied}
                  setCopied={setGoCopied}
                  text={goSnippet}
                  variant="outlined"
                >
                  Copy Golang
                </CopyClipboardBtn>
              </Box>
            </Box>
          )}
        </StyledDrawerContainer>
      </StyledDrawer>
    </>
  );
}

export default LeadStatusRuleSidebar;
